import React, { useState, useRef, useEffect } from "react";
import { FileText, CircleCheck } from "lucide-react";
import { Card, Combobox, Skeleton } from "@/components/ui";
import { useShareFormLink } from "../../context/ShareFormLinkContext";

export function StepChooseForm() {
  const {
    formSelectItems,
    selectedFormId,
    setSelectedFormId,
    isLoadingForms,
    initialFormId,
    defaultRequest,
    isLoadingDefault,
  } = useShareFormLink();

  // local selected form option state
  const [formSource, setFormSource] = useState<"standard" | "custom">("standard");
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!isLoadingDefault && formSelectItems.length > 0 && !hasInitializedRef.current) {
      const defaultFormId = defaultRequest?.form?.id || (defaultRequest as any)?.formId;
      const targetFormId =
        initialFormId !== undefined && initialFormId !== null ? String(initialFormId) : selectedFormId;
      const isDefault =
        !targetFormId ||
        (defaultFormId && String(targetFormId) === String(defaultFormId)) ||
        (!defaultFormId && String(targetFormId) === String(formSelectItems[0]?.value));
      setFormSource(isDefault ? "standard" : "custom");
      hasInitializedRef.current = true;
    }
  }, [selectedFormId, formSelectItems, initialFormId, isLoadingDefault, defaultRequest]);

  const selectStandardForm = () => {
    setFormSource("standard");
    const defaultFormId = defaultRequest?.form?.id || (defaultRequest as any)?.formId;
    if (defaultFormId) {
      setSelectedFormId(String(defaultFormId));
    } else if (formSelectItems[0]) {
      setSelectedFormId(formSelectItems[0].value);
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-in fade-in duration-300">
      <div className="flex flex-col gap-1.5">
        <div className="font-bold text-slate-800 text-lg">Step 2: Confirm Form</div>
        <div className="text-sm text-slate-500 font-medium leading-relaxed">
          Select the form that fans will see when they open your review link.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3.5">
        {/* Option A: Standard Form */}
        <Card
          onClick={selectStandardForm}
          className={`p-4 border rounded-xl cursor-pointer flex flex-row items-center justify-between gap-4 transition-all ${
            formSource === "standard"
              ? "border-primary bg-primary/5 shadow-sm"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center gap-3.5 min-w-0 flex-1">
            <div
              className={`p-3 rounded-xl border shrink-0 ${
                formSource === "standard"
                  ? "border-primary/20 bg-primary/10 text-primary"
                  : "border-slate-200 bg-slate-50 text-slate-500"
              }`}
            >
              <FileText className="size-5" />
            </div>
            <div className="flex flex-col min-w-0 text-left">
              <span className="text-sm font-bold text-slate-800 leading-tight">Use Standard Review Form</span>
              <span className="text-sm text-slate-500 font-medium leading-relaxed mt-1">
                Our recommended standard review survey. Perfect for most situations.
              </span>
            </div>
          </div>
          <div
            className={`size-6 rounded-full border flex items-center justify-center shrink-0 ml-auto ${
              formSource === "standard" ? "border-primary bg-primary text-white" : "border-slate-300"
            }`}
          >
            {formSource === "standard" && <CircleCheck className="size-4 stroke-[3]" />}
          </div>
        </Card>

        {/* Option B: Choose Specific Form */}
        <Card
          onClick={() => setFormSource("custom")}
          className={`p-4 border rounded-xl cursor-pointer flex flex-col gap-4 transition-all ${
            formSource === "custom"
              ? "border-primary bg-primary/5 shadow-sm"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex items-center gap-3.5 min-w-0 flex-1">
              <div
                className={`p-3 rounded-xl border shrink-0 ${
                  formSource === "custom"
                    ? "border-primary/20 bg-primary/10 text-primary"
                    : "border-slate-200 bg-slate-50 text-slate-500"
                }`}
              >
                <FileText className="size-5" />
              </div>
              <div className="flex flex-col min-w-0 text-left">
                <span className="text-sm font-bold text-slate-800 leading-tight">Choose a different form</span>
                <span className="text-sm text-slate-500 font-medium leading-relaxed mt-1">
                  Select from your other feedback forms.
                </span>
              </div>
            </div>
            <div
              className={`size-6 rounded-full border flex items-center justify-center shrink-0 ml-auto ${
                formSource === "custom" ? "border-primary bg-primary text-white" : "border-slate-300"
              }`}
            >
              {formSource === "custom" && <CircleCheck className="size-4 stroke-[3]" />}
            </div>
          </div>

          {formSource === "custom" && (
            <div className="w-full animate-in slide-in-from-top-1 duration-200" onClick={e => e.stopPropagation()}>
              {!isLoadingForms && formSelectItems.length > 0 ? (
                <Combobox
                  items={formSelectItems}
                  value={formSelectItems.find(i => i.value === selectedFormId) || null}
                  onValueChange={(val: any) => val && setSelectedFormId(val.value)}
                  searchable
                  placeholder="Select custom feedback form..."
                  emptyText="No forms found"
                  isItemEqualToValue={(current, selected) => current?.value === selected?.value}
                  itemToStringLabel={item => item?.label || ""}
                  className="text-sm font-semibold text-slate-700 bg-white border border-slate-200 w-full flex items-center justify-between h-10 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer outline-none"
                />
              ) : (
                <Skeleton className="h-10 w-full rounded-lg" />
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
